import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useBrands } from "../contexts/BrandContext";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { XCircle, Clock, Building2, MapPin, CalendarDays, Inbox } from "lucide-react";
import { formatDateFr } from "../lib/dateFormat";
import { effectiveShiftHours } from "../lib/shiftHours";

const pad2 = (n) => String(n).padStart(2, "0");
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/**
 * Historique des absences (shifts) d'un collaborateur :
 * Cette section liste UNIQUEMENT les postes planifiés marqués comme
 * "Non effectué" (`validation_status === "missed"`) ou déclarés en
 * absence via le raccourci `Marquer en absence`
 * (`absence_status === "absent"`).
 *
 * Elle est distincte de l'onglet "Historique des indisponibilités"
 * qui, lui, agrège les congés et autres arrêts (leaves).
 */
export default function CollaborateurAbsencesHistory({ collaborateurId }) {
  const { brands } = useBrands();
  const year = new Date().getFullYear();
  const [range, setRange] = useState({
    start_date: `${year}-01-01`,
    end_date: `${year}-12-31`,
  });
  const [shifts, setShifts] = useState([]);
  const [sites, setSites] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const today = todayISO();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!collaborateurId) return;
      setLoading(true);
      try {
        const [shRes, sitesRes, catsRes] = await Promise.all([
          api.get("/shifts", {
            params: {
              collaborateur_id: collaborateurId,
              start_date: range.start_date,
              end_date: range.end_date,
            },
          }),
          api.get("/sites").catch(() => ({ data: [] })),
          api.get("/shift-categories").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setShifts(shRes.data || []);
        setSites(sitesRes.data || []);
        setCategories(catsRes.data || []);
      } catch {
        if (!cancelled) setShifts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [collaborateurId, range.start_date, range.end_date]);

  const { absences, missedCount, absentCount, totalHours } = useMemo(() => {
    const items = shifts
      .filter((s) => {
        const vs = (s.validation_status || "").toLowerCase();
        const abs = (s.absence_status || "").toLowerCase();
        return vs === "missed" || abs === "absent";
      })
      .sort((a, b) => {
        const k = (b.date || "").localeCompare(a.date || "");
        return k !== 0 ? k : (b.start_time || "").localeCompare(a.start_time || "");
      });
    let mC = 0, aC = 0, h = 0;
    items.forEach((s) => {
      const vs = (s.validation_status || "").toLowerCase();
      const abs = (s.absence_status || "").toLowerCase();
      if (vs === "missed") mC += 1;
      if (abs === "absent") aC += 1;
      h += effectiveShiftHours(s);
    });
    return {
      absences: items,
      missedCount: mC,
      absentCount: aC,
      totalHours: Math.round(h * 10) / 10,
    };
  }, [shifts]);

  const enrich = (s) => {
    const brand = brands.find((b) => b.id === s.brand_id);
    const site = sites.find((x) => x.id === s.site_id);
    const category = categories.find((c) => c.id === s.shift_category_id);
    return { brand, site, category };
  };

  const badgeFor = (s) => {
    const vs = (s.validation_status || "").toLowerCase();
    const abs = (s.absence_status || "").toLowerCase();
    // Priorité à "absent" (déclaré) sur "missed" (validation post-mortem)
    if (abs === "absent") {
      return { label: "Absent", cls: "bg-amber-100 text-amber-800 border-amber-200" };
    }
    if (vs === "missed") {
      return { label: "Non effectué", cls: "bg-rose-100 text-rose-800 border-rose-200" };
    }
    return { label: "—", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" };
  };

  const renderRow = (s) => {
    const { brand, site, category } = enrich(s);
    const badge = badgeFor(s);
    const isPast = (s.date || "") < today;
    return (
      <div
        key={s.id}
        className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3 bg-white"
        data-testid={`absence-row-${s.id}`}
      >
        <div className="h-12 w-12 rounded-sm flex flex-col items-center justify-center flex-shrink-0 border bg-rose-50 border-rose-200 text-rose-700">
          <span className="text-[10px] font-mono leading-none">
            {s.date
              ? new Date(s.date + "T12:00:00")
                  .toLocaleDateString("fr-FR", { month: "short" })
                  .replace(".", "")
              : "—"}
          </span>
          <span className="text-base font-bold leading-tight">
            {s.date ? s.date.slice(8, 10) : "—"}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-zinc-900">{formatDateFr(s.date)}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-1 text-xs font-mono text-zinc-600">
            <p className="inline-flex items-center gap-1.5">
              <Clock size={11} className="text-zinc-400" /> {s.start_time} → {s.end_time}
            </p>
            <p className="inline-flex items-center gap-1.5">
              <Building2 size={11} className="text-zinc-400" /> {brand?.name || "—"}
            </p>
            <p className="inline-flex items-center gap-1.5">
              <MapPin size={11} className="text-zinc-400" /> {site?.name || "Site non précisé"}
            </p>
            {category && (
              <p className="inline-flex items-center gap-1.5">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: category.color || "#0055FF" }}
                />
                {category.name}
              </p>
            )}
          </div>
          {(s.role_label || s.poste) && (
            <p className="text-[11px] text-zinc-500 mt-1">
              Poste : {s.role_label || s.poste}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span
            className={`text-[10px] uppercase font-mono tracking-wider px-2 py-0.5 rounded-sm border ${badge.cls}`}
            data-testid={`absence-row-badge-${s.id}`}
          >
            {badge.label}
          </span>
          <span
            className={`text-[10px] uppercase font-mono tracking-wider px-2 py-0.5 rounded-sm ${
              isPast ? "bg-zinc-100 text-zinc-600" : "bg-sky-100 text-sky-700"
            }`}
          >
            {isPast ? "Passé" : "À venir"}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="collab-absences-history">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="label-uppercase mb-1.5 block">Période — du</Label>
          <Input
            type="date"
            value={range.start_date}
            onChange={(e) => setRange({ ...range, start_date: e.target.value })}
            data-testid="absences-start-date"
          />
        </div>
        <div>
          <Label className="label-uppercase mb-1.5 block">au</Label>
          <Input
            type="date"
            value={range.end_date}
            onChange={(e) => setRange({ ...range, end_date: e.target.value })}
            data-testid="absences-end-date"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid="absences-kpi-total">
          <div className="flex items-center gap-2 mb-2">
            <XCircle size={14} className="text-rose-600" />
            <p className="label-uppercase">Absences totales</p>
          </div>
          <p className="text-2xl font-display font-bold tracking-tight">{absences.length}</p>
          <p className="text-[11px] text-zinc-500 font-mono mt-1">
            {missedCount} non effectué{missedCount > 1 ? "s" : ""} · {absentCount} déclarée{absentCount > 1 ? "s" : ""}
          </p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid="absences-kpi-hours">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={14} className="text-amber-600" />
            <p className="label-uppercase">Heures non effectuées</p>
          </div>
          <p className="text-2xl font-display font-bold tracking-tight">{totalHours} h</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid="absences-kpi-latest">
          <div className="flex items-center gap-2 mb-2">
            <CalendarDays size={14} className="text-zinc-600" />
            <p className="label-uppercase">Dernière absence</p>
          </div>
          <p className="text-sm font-mono text-zinc-900">
            {absences.length > 0 ? formatDateFr(absences[0].date) : "—"}
          </p>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
          <p className="label-uppercase">Historique des absences</p>
          <span className="text-[11px] font-mono text-zinc-500">
            {loading ? "Chargement…" : `${absences.length} entrée${absences.length > 1 ? "s" : ""}`}
          </span>
        </div>
        <div className="divide-y divide-zinc-100 max-h-[560px] overflow-y-auto">
          {!loading && absences.length === 0 && (
            <div className="py-12 text-center" data-testid="absences-empty">
              <Inbox className="mx-auto text-zinc-300 mb-3" size={36} />
              <p className="text-sm text-zinc-500 font-mono">
                Aucune absence enregistrée sur la période sélectionnée.
              </p>
            </div>
          )}
          {absences.map(renderRow)}
        </div>
      </div>
    </div>
  );
}
